import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { FlightDetails, ConnectionFlight } from "./types";
import { Search } from "lucide-react";
import MicIcon from "assets/images/mic-icon.png";
import {
  fetchFlights,
  fetchFlightSuggestions,
  FlightSuggestion,
  FlightSuggestionDirectiveFilters,
  getLastFlightNumber,
  getRecentSearches,
  saveRecentSearch,
  RecentSearchItem,
} from "apis/loadsServices";
import { useFlightNumbers } from "./useFlightNumbers";
import SearchResults from "./SearchResults";
import { useGlobalLoader } from "contexts/GlobalLoaderContext";
import { useToastStore } from "store/toastStore";
import "react-datepicker/dist/react-datepicker.css";
import {
  formatDateLocal,
  saveLastFlightSearchToStorage,
  loadLastFlightSearchFromStorage,
  LastFlightSearchData,
} from "./searchStorage";
import {
  withDirectFlightCompositeIds,
  calculateEffectiveTotalFlights,
  normalizeFlightNumber,
  mapStopsFilterToDisplayIntent,
} from "./routeSearchHelpers";
import DateRangeCalendar from "./DateRangeCalendar";
import RoutePager from "./RoutePager";
import FilterSortBar, { DurationSortOrder } from "./FilterSortBar";
import RecentSearchesDropdown from "./RecentSearchesDropdown";
import AiSuggestiveFilters from "./AiSuggestiveFilters";
import { useClickOutside } from "../../hooks/useClickOutside";

// Custom styles for DatePicker
const datePickerStyles = `
  .react-datepicker__day--highlighted {
    background-color: #FFD700 !important;
    color: #000 !important;
  }
  .react-datepicker__day--highlighted:hover {
    background-color: #FFC700 !important;
  }
  /* react-datepicker tracks the last-clicked day as "keyboard-selected"
     independently of our own highlightDates. Clicking an already-selected
     day to remove it clears our highlight but the library still paints it
     as keyboard-selected until another day is clicked. Suppress that style
     unless the day is also one we actually still consider selected. */
  .react-datepicker__day--keyboard-selected:not(.react-datepicker__day--highlighted) {
    background-color: transparent !important;
    color: inherit !important;
  }
`;

interface SearchFlightsNumberProps {
  onShowSearchResults?: (show: boolean) => void;
  onPostRequestSuccess?: (
    optimisticItems?: any[],
    options?: { keepResults?: boolean },
  ) => void;
  tabButtons?: React.ReactNode;
  onBack?: () => void;
  readOnly?: boolean;
  breadcrumbLabel?: string;
  /** Voice heard a flight number on the Route tab and switched here — `nonce` changes each time so a repeat value re-applies. */
  voicePrefill?: { flightNumber: string; nonce: number } | null;
}



const SearchFlightsNumber: React.FC<SearchFlightsNumberProps> = ({
  onShowSearchResults,
  onPostRequestSuccess,
  tabButtons,
  onBack,
  readOnly = false,
  breadcrumbLabel = "Loads",
  voicePrefill,
}) => {
  const { showToast } = useToastStore.getState();
  const [showResults, setShowResults] = useState<boolean>(false);
  const [flightNumber, setFlightNumber] = useState<string>("");

  // Apply once per voice hand-off (nonce guards against re-applying the same
  // value on an unrelated re-render).
  const appliedVoiceNonceRef = useRef<number | null>(null);
  // Guards against a slower, stale FlightSuggestions response (e.g. from the
  // initial mixed-results load) overwriting a newer one fired by a filter change.
  const suggestionsRequestSeq = useRef(0);
  useEffect(() => {
    if (!voicePrefill || appliedVoiceNonceRef.current === voicePrefill.nonce) return;
    appliedVoiceNonceRef.current = voicePrefill.nonce;
    setFlightNumber(voicePrefill.flightNumber);
  }, [voicePrefill]);
  const [showFlightSuggestions, setShowFlightSuggestions] = useState(false);
  const flightFieldRef = useRef<HTMLDivElement | null>(null);
  const [date, setDate] = useState<string>(
    new Date(Date.now() + 86400000).toISOString().split("T")[0]
  ); // Default to Tomorrow's date
  const [flights, setFlights] = useState<FlightDetails[]>([]);
  const [connectionFlights, setConnectionFlights] = useState<ConnectionFlight[]>([]);
  // Set when the flight search request itself fails (e.g. a 500), as
  // distinct from a successful response with zero results.
  const [searchError, setSearchError] = useState<string | null>(null);
  // The API's own message for a valid zero-result response.
  const [noResultsMessage, setNoResultsMessage] = useState<string | null>(
    null,
  );
  const [stopsFilter, setStopsFilter] = useState<"none" | "one-or-fewer" | "one" | "two" | "three" | null>("none");
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<DurationSortOrder | string | null>(null);
  const [suggestions, setSuggestions] = useState<FlightSuggestion[]>([]);
  const [appliedSuggestionId, setAppliedSuggestionId] = useState<string | null>(null);
  const [appliedSuggestion, setAppliedSuggestion] = useState<FlightSuggestion | null>(null);
  const [facets, setFacets] = useState<any>(null);
  const [appliedDirectiveFilters, setAppliedDirectiveFilters] = useState<FlightSuggestionDirectiveFilters | null>(null);
  const [priorManualState, setPriorManualState] = useState<{
    stopsFilter: "none" | "one-or-fewer" | "one" | "two" | "three" | null;
    sortBy: string | null;
    sortOrder: DurationSortOrder | string | null;
    directiveFilters: FlightSuggestionDirectiveFilters | null;
  } | null>(null);
  const [isLoadingFlights, setIsLoadingFlights] = useState<boolean>(false);
  const [isLoadingLastSearch, setIsLoadingLastSearch] = useState(false);
  const { showLoader, hideLoader } = useGlobalLoader();
  const [errors, setErrors] = useState<{
    flightNumber?: string;
    date?: string;
    apiResponse?: string;
  }>({});
  const dateInputRef = useRef<HTMLInputElement | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  // Track if we're auto-resetting to prevent infinite loops
  const isAutoResetting = useRef<boolean>(false);
  // Tracks the last page we fetched, so we refetch on ANY page change
  // (including going back to page 1), but not on initial load / search reset.
  const prevPageNumber = useRef<number | null>(null);
  const [PageNumber, setPageNumber] = useState(1);
  const [totalFlights, setTotalFlights] = useState(0);
  // API response's top-level `count` field, used only for the
  // "Select all N Results" label.
  const [apiResultCount, setApiResultCount] = useState(0);
  // Raw direct/connection totals from the API, used to distinguish
  // "no direct flights (but connections exist)" from "no flights at all".
  const [directTotal, setDirectTotal] = useState<number | undefined>(undefined);
  const [connectionTotal, setConnectionTotal] = useState<number | undefined>(undefined);
  // Bumped to force the Filter dropdown open when "Widen search" is clicked.
  const [openFilterSignal, setOpenFilterSignal] = useState<number | undefined>(
    undefined,
  );
  const [range, setRange] = useState<[Date | null, Date | null]>([null, null]);
  const [datesInRange, setDatesInRange] = useState<string[]>([]);
  const [startDate, endDate] = range;
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Recent searches (flight number) state
  const [recentFlightNumberSearches, setRecentFlightNumberSearches] =
    useState<RecentSearchItem[]>([]);
  const [isLoadingRecentSearches, setIsLoadingRecentSearches] =
    useState(false);
  const [isRecentSearchesOpen, setIsRecentSearchesOpen] = useState(false);

  const flightsPerPage = 9;

  // A Week/Month calendar selection sends a date RANGE (not a single day) —
  // the API is asked for more results per page (50) for those searches so a
  // wide date range doesn't take dozens of round trips. `totalPages` and the
  // "select all" prefetch below must use this SAME page size the API request
  // actually uses, or the UI ends up offering more pages than really exist
  // (computed off 9-per-page) while asking the API for 50-per-page — pages
  // past the real end come back empty even though the UI still shows more.
  const isMonthOrWeekSearch = useMemo(
    () =>
      datesInRange.some((d) => {
        if (!d.includes(" - ")) return false;
        const [startStr, endStr] = d.split(" - ");
        const dStart = new Date(`${startStr}T00:00:00`);
        const dEnd = new Date(`${endStr}T00:00:00`);
        if (isNaN(dStart.getTime()) || isNaN(dEnd.getTime())) return false;
        const diffDays = Math.round(
          (dEnd.getTime() - dStart.getTime()) / (1000 * 3600 * 24),
        );
        return diffDays > 14;
      }),
    [datesInRange],
  );
  const effectivePageSize = isMonthOrWeekSearch ? 50 : flightsPerPage;
  const totalPages = Math.ceil(totalFlights / effectivePageSize);

  // Flight-number autocomplete data: full list fetched once and cached,
  // filtered locally as the user types (no debounce needed).
  const { isLoadingFlightNumbers, filterFlightNumbers } = useFlightNumbers();
  const flightNumberOptions = useMemo(
    () =>
      filterFlightNumbers(flightNumber).map((value) => ({
        value,
        label: value,
      })),
    [filterFlightNumbers, flightNumber],
  );

  // Close the flight-number suggestions when clicking outside the field
  useClickOutside(flightFieldRef, () => setShowFlightSuggestions(false));

  const handleStopsFilterChange = (filter: "none" | "one-or-fewer" | "one" | "two" | "three" | null) => {
    if (!filter) return;
    setAppliedSuggestionId(null);
    setAppliedSuggestion(null);
    setStopsFilter(filter);
    // If we have already searched, trigger a new search with the updated filter
    if (hasSearched) {
      getFlights({ stopsFilter: filter });
    }
  };

  const handleSortChange = (
    newSortBy: string | null,
    newSortOrder: DurationSortOrder | string | null,
  ) => {
    setAppliedSuggestionId(null);
    setAppliedSuggestion(null);
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    if (hasSearched) {
      getFlights({ sortBy: newSortBy, sortOrder: newSortOrder });
    }
  };

  const handleSelectSuggestion = (suggestion: FlightSuggestion) => {
    if (!appliedSuggestionId) {
      setPriorManualState({
        stopsFilter,
        sortBy,
        sortOrder,
        directiveFilters: appliedDirectiveFilters,
      });
    }

    const directive = suggestion.directive || {};
    const newDirectiveFilters = directive.filters || {};

    let newSortBy = sortBy;
    if (directive.sortBy !== undefined && directive.sortBy !== null) {
      newSortBy = directive.sortBy;
    }

    let newSortOrder = sortOrder;
    if (directive.sortDirection !== undefined && directive.sortDirection !== null) {
      newSortOrder = directive.sortDirection;
    }

    let newStopsFilter = stopsFilter;
    if (typeof newDirectiveFilters.stops === "number") {
      if (newDirectiveFilters.stops === 0) newStopsFilter = "none";
      else if (newDirectiveFilters.stops === 1) newStopsFilter = "one";
      else if (newDirectiveFilters.stops === 2) newStopsFilter = "two";
      else if (newDirectiveFilters.stops === 3) newStopsFilter = "three";
    } else if (
      newDirectiveFilters.includeDirect === true &&
      newDirectiveFilters.includeConnections === false
    ) {
      newStopsFilter = "none";
    } else if (
      newDirectiveFilters.includeDirect === false &&
      newDirectiveFilters.includeConnections === true
    ) {
      newStopsFilter = "one-or-fewer";
    } else if (
      newDirectiveFilters.includeDirect === true &&
      newDirectiveFilters.includeConnections === true
    ) {
      newStopsFilter = "none";
    }

    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setStopsFilter(newStopsFilter);
    setAppliedDirectiveFilters(newDirectiveFilters);
    setAppliedSuggestionId(suggestion.id);
    setAppliedSuggestion(suggestion);
    setPageNumber(1);
    prevPageNumber.current = 1;

    setIsLoadingFlights(true);
    getFlights({
      pageNumber: 1,
      stopsFilter: newStopsFilter,
      sortBy: newSortBy,
      sortOrder: newSortOrder,
      directiveFilters: newDirectiveFilters,
      appliedSuggestionId: suggestion.id,
    });
  };

  // Clears only the applied AI suggestion, leaving the current stops
  // filter/sort/directive filters as-is.
  const handleDismissSuggestion = () => {
    setAppliedSuggestionId(null);
    setAppliedSuggestion(null);
    setPriorManualState(null);
    setPageNumber(1);
    prevPageNumber.current = 1;

    setIsLoadingFlights(true);
    getFlights({
      pageNumber: 1,
      stopsFilter,
      sortBy,
      sortOrder,
      directiveFilters: appliedDirectiveFilters,
      appliedSuggestionId: null,
    });
  };

  // Fetch recent searches (server-backed) on mount.
  const fetchRecentFlightNumberSearches = async () => {
    setIsLoadingRecentSearches(true);
    try {
      const items = await getRecentSearches(10);
      setRecentFlightNumberSearches(items);
    } finally {
      setIsLoadingRecentSearches(false);
    }
  };

  useEffect(() => {
    fetchRecentFlightNumberSearches();
  }, []);

  // Selecting a recent search: populate the Flight No. field and, if no date
  // has been chosen yet, open the calendar — mirroring manual selection.
  const handleRecentSearchSelect = (item: RecentSearchItem) => {
    if (!item.flightNumber) return;

    setFlightNumber(item.flightNumber);
    setShowFlightSuggestions(false);
    setErrors((prev) => ({
      ...prev,
      flightNumber: undefined,
      apiResponse: undefined,
    }));

    if (selectedDates.length === 0) {
      setTimeout(() => setIsCalendarOpen(true), 100);
    }

    fetchRecentFlightNumberSearches();
  };

  // Fetch flights when the page actually changes (including back to page 1),
  // but not on initial load or when a new search resets the page.
  useEffect(() => {
    if (
      hasSearched &&
      prevPageNumber.current !== null &&
      prevPageNumber.current !== PageNumber
    ) {
      const fetchFlightsForPage = async () => {
        try {
          await getFlights();

          // Scroll to search results after page change
          setTimeout(() => {
            const searchResultsElement =
              document.getElementById("search-results");
            if (searchResultsElement) {
              searchResultsElement.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }
          }, 100);
        } catch (error) {
          console.error("Error fetching flights for page:", error);
        }
      };
      fetchFlightsForPage();
    }
    // Remember the page we just handled.
    if (hasSearched) {
      prevPageNumber.current = PageNumber;
    }
  }, [PageNumber, hasSearched]);

  // Auto-reset filter to "none" when "one-or-fewer" returns no valid connection flights
  useEffect(() => {
    if (
      hasSearched &&
      stopsFilter === "one-or-fewer" &&
      !isLoadingFlights &&
      !isAutoResetting.current
    ) {
      // Check if there are any valid connection flights (must have at least 2 segments)
      const hasValidConnections = connectionFlights.some(
        (connection) => connection.segments && connection.segments.length >= 2
      );
      
      // If no valid connection flights and no direct flights, revert to non-stop
      if (!hasValidConnections && flights.length === 0) {
        // Prevent infinite loops by setting the flag
        isAutoResetting.current = true;
        // Reset filter to "none" and trigger a new search
        setStopsFilter("none");
        // Reset the flag after a short delay to allow the filter change to process
        setTimeout(() => {
          isAutoResetting.current = false;
        }, 100);
      }
    }
  }, [flights.length, connectionFlights, stopsFilter, hasSearched, isLoadingFlights]);

  const handleLastSearchClick = async () => {
    setIsLoadingLastSearch(true);
    try {
      // First check localStorage
      let lastSearchData: LastFlightSearchData | null =
        loadLastFlightSearchFromStorage();

      // If not found in localStorage, fetch from API as fallback
      if (!lastSearchData) {
        showLoader(); // Show global loader for API call
        try {
          const { lastSearchFlightNumber } = await getLastFlightNumber();
          if (lastSearchFlightNumber) {
            // Convert UTC date to YYYY-MM-DD format
            const lastSearchDate = new Date(lastSearchFlightNumber.date)
              .toISOString()
              .split("T")[0];

            lastSearchData = {
              flightNumber: lastSearchFlightNumber.flightNumber,
              dates: [lastSearchDate],
              savedAt: Date.now(),
            };

            // Save to localStorage for future use
            saveLastFlightSearchToStorage(
              lastSearchFlightNumber.flightNumber,
              [lastSearchDate],
            );
          } else {
            hideLoader();
            return;
          }
        } catch (error) {
          console.error("Error fetching last search from API:", error);
          hideLoader();
          return;
        } finally {
          hideLoader();
        }
      }

      // Populate fields with stored data
      if (lastSearchData) {
        const { flightNumber, dates } = lastSearchData;

        setFlightNumber(flightNumber);

        // Convert date strings to Date objects for calendar
        const dateObjects = dates.map((dateStr) => new Date(dateStr));
        setSelectedDates(dateObjects);
        setDatesInRange(dates);

        setErrors({}); // Clear any existing errors

        // Trigger search
        setHasSearched(true);
        onShowSearchResults?.(true);
        prevPageNumber.current = 1; // mark page 1 so the effect doesn't refetch
        setPageNumber(1);

        try {
          showLoader();
          // The Filter/Sort chosen up front in the search form carry into
          // this search rather than resetting.
          await getFlights({ flightNumber, dates, pageNumber: 1 });

          // Scroll to search results
          setTimeout(() => {
            const searchResultsElement =
              document.getElementById("search-results");
            if (searchResultsElement) {
              searchResultsElement.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }
          }, 100);
        } catch (error) {
          console.error("Error fetching flights for last search:", error);
        } finally {
          hideLoader();
        }
      }
    } catch (error) {
      console.error("Failed to load last search:", error);
    } finally {
      setIsLoadingLastSearch(false);
    }
  };

  const getFlights = async (overrideParams?: {
    stopsFilter?: "none" | "one-or-fewer" | "one" | "two" | "three" | null;
    flightNumber?: string;
    dates?: string[];
    pageNumber?: number;
    sortBy?: string | null;
    sortOrder?: DurationSortOrder | string | null;
    directiveFilters?: FlightSuggestionDirectiveFilters | null;
    appliedSuggestionId?: string | null;
    skipSuggestionsFetch?: boolean;
  }) => {
    setIsLoadingFlights(true);
    setSearchError(null);
    setNoResultsMessage(null);
    try {
      const currentFilter = overrideParams?.stopsFilter ?? stopsFilter;
      const currentSortBy =
        overrideParams?.sortBy !== undefined ? overrideParams.sortBy : sortBy;
      const currentSortOrder =
        overrideParams?.sortOrder !== undefined
          ? overrideParams.sortOrder
          : sortOrder;
      const activeDirectiveFilters =
        overrideParams?.directiveFilters !== undefined
          ? overrideParams.directiveFilters
          : appliedDirectiveFilters;
      const activeAppliedSuggestionId =
        overrideParams?.appliedSuggestionId !== undefined
          ? overrideParams.appliedSuggestionId
          : appliedSuggestionId;

      let Stops: number | undefined;
      let IncludeDirect: boolean | undefined;
      let IncludeConnections: boolean | undefined;

      if (currentFilter === "none") {
        // Always fetch connections alongside direct flights so we can tell
        // "no direct flights, but connections exist" apart from "no flights
        // at all" using the API's total/connectionTotal. Deliberately omits
        // Stops: the API applies it to connections too, so Stops: 0 would
        // zero out connectionTotal even with IncludeConnections true. Direct
        // results are non-stop by definition regardless.
        IncludeDirect = true;
        IncludeConnections = true;
      } else if (currentFilter === "one-or-fewer") {
        IncludeDirect = false;
        IncludeConnections = true;
      } else if (currentFilter === "one") {
        Stops = 1;
      } else if (currentFilter === "two") {
        Stops = 2;
      } else if (currentFilter === "three") {
        Stops = 3;
      } else {
        // Default: non-stop only
        IncludeDirect = true;
        IncludeConnections = false;
      }

      if (activeDirectiveFilters) {
        if (activeDirectiveFilters.includeDirect !== undefined) {
          IncludeDirect = activeDirectiveFilters.includeDirect;
        }
        if (activeDirectiveFilters.includeConnections !== undefined) {
          IncludeConnections = activeDirectiveFilters.includeConnections;
        }
        if (activeDirectiveFilters.stops !== undefined) {
          Stops = activeDirectiveFilters.stops;
        }
      }

      const datesValue = overrideParams?.dates ?? datesInRange;
      const pageNumberValue = overrideParams?.pageNumber ?? PageNumber;
      const isMonthSearch = datesValue.some((d) => {
        if (!d.includes(" - ")) return false;
        const [startStr, endStr] = d.split(" - ");
        const dStart = new Date(`${startStr}T00:00:00`);
        const dEnd = new Date(`${endStr}T00:00:00`);
        if (isNaN(dStart.getTime()) || isNaN(dEnd.getTime())) return false;
        const diffDays = Math.round(
          (dEnd.getTime() - dStart.getTime()) / (1000 * 3600 * 24)
        );
        return diffDays > 14;
      });

      const flightNumberValue = normalizeFlightNumber(
        overrideParams?.flightNumber ?? flightNumber,
      );
      const params = {
        FlightNumber: flightNumberValue,
        Dates: datesValue,
        PageNumber: pageNumberValue,
        PageSize: isMonthSearch ? 50 : flightsPerPage,
        IncludeDirect,
        IncludeConnections,
        ...(Stops !== undefined ? { Stops } : {}),
        ...(currentSortBy ? { SortBy: currentSortBy } : {}),
        ...(currentSortOrder ? { SortOrder: currentSortOrder } : {}),
        ...(activeDirectiveFilters?.leaveTime
          ? { LeaveTime: activeDirectiveFilters.leaveTime }
          : {}),
        ...(activeDirectiveFilters?.arrivalTime
          ? { ArrivalTime: activeDirectiveFilters.arrivalTime }
          : {}),
        ...(activeDirectiveFilters?.airlineName
          ? { AirlineName: activeDirectiveFilters.airlineName }
          : {}),
      };
      const response = await fetchFlights(params);
      const rawFlightData = response.data.items || [];
      const connectionsData = (response.data.connections || []) as ConnectionFlight[];

      // Check if we requested connections but got direct flights instead
      // This happens when API returns direct flights in items array when IncludeConnections: true
      if (currentFilter === "one-or-fewer" && connectionsData.length === 0 && rawFlightData.length > 0) {
        // Check if rawFlightData contains direct flights (stops: 0 or intermediateAirports: [])
        // The API response has 'stops' field in the raw data
        const hasDirectFlights = rawFlightData.some(
          (flight: any) => {
            return (flight.stops === 0 || flight.stops === undefined) ||
                   (flight.intermediateAirports && Array.isArray(flight.intermediateAirports) && flight.intermediateAirports.length === 0);
          }
        );
        
        // If we got direct flights instead of connections, we need to revert to non-stop
        if (hasDirectFlights && !isAutoResetting.current) {
          isAutoResetting.current = true;
          setStopsFilter("none");
          // Don't process this response, let the filter change trigger a new search
          setIsLoadingFlights(false);
          setTimeout(() => {
            isAutoResetting.current = false;
          }, 100);
          return;
        }
      }
      
      const flightData = rawFlightData as FlightDetails[];

      // A valid zero-result response — surface the API's own explanation.
      setNoResultsMessage(
        flightData.length === 0 && connectionsData.length === 0
          ? response.message || null
          : null,
      );

      const flightsWithCompositeIds = withDirectFlightCompositeIds(flightData);

      // Generate composite IDs for connection flights
      const connectionsWithCompositeIds = connectionsData.map((connection) => {
        // Generate composite IDs for each segment
        const segmentsWithIds = connection.segments.map((segment) => {
          const baseId = segment.id ?? `${Date.now()}-${Math.random()}`;
          const departureTime = segment.departureTime;
          const compositeId = departureTime
            ? `${baseId}_${departureTime.replace(/[^0-9]/g, "")}`
            : String(baseId);

          return {
            ...segment,
            compositeId,
          };
        });

        // Generate composite ID from ALL segments — two itineraries can share
        // the same first leg and total duration but differ in later legs, so
        // keying on just the first segment made them collide (selecting a shared
        // leg in one card also selected it in the other).
        const connectionCompositeId = segmentsWithIds.length
          ? `conn_${segmentsWithIds.map((s) => s.compositeId).join("_")}`
          : `conn_${connection.totalDuration}`;

        return {
          ...connection,
          segments: segmentsWithIds,
          compositeId: connectionCompositeId,
        };
      });

      setFlights(flightsWithCompositeIds);
      setConnectionFlights(connectionsWithCompositeIds);
      setApiResultCount(response.count ?? 0);

      // Total = direct single-flight results (data.total) + multi-leg
      // connections (connectionTotal). A 1/2/3-stop search returns BOTH, so the
      // count is their sum; the missing side counts as 0.
      const responseDirectTotal = response.data.total ?? 0;
      const responseConnTotal = response.data.connectionTotal ?? 0;
      setDirectTotal(response.data.total);
      setConnectionTotal(response.data.connectionTotal);

      if (
        response.data.total !== undefined ||
        response.data.connectionTotal !== undefined
      ) {
        const activeFilter = overrideParams?.stopsFilter ?? stopsFilter;
        setTotalFlights(
          calculateEffectiveTotalFlights(
            activeFilter,
            response.data.total,
            response.data.connectionTotal,
          ),
        );
      } else {
        // Fallback: estimate from this page's results (direct + connections).
        // Must use the SAME page size as the request above (50 for
        // month/week searches) or the estimate — and the totalPages derived
        // from it — drifts from what was actually requested.
        const requestPageSize = isMonthSearch ? 50 : flightsPerPage;
        const pageCount = flightData.length + connectionsData.length;
        const estimatedTotal =
          pageCount < requestPageSize
            ? (PageNumber - 1) * requestPageSize + pageCount
            : PageNumber * requestPageSize; // minimum estimate
        setTotalFlights(estimatedTotal);
      }

      setShowResults(flightData.length > 0 || connectionsData.length > 0);

      // Fetch flight suggestions in background on initial search / material filter changes (never on pagination alone)
      if (pageNumberValue === 1 && !overrideParams?.skipSuggestionsFetch) {
        const responseFacets =
          (response as any)?.facets ||
          (response as any)?.data?.facets ||
          (response as any)?.Facets ||
          (response as any)?.data?.Facets ||
          null;

        console.log("[FlightSearch Response facets]", responseFacets);
        setFacets(responseFacets);

        // IncludeDirect/IncludeConnections/Stops above are the raw over-fetch
        // params (see mapStopsFilterToParams — "none" deliberately asks for
        // connections too, then the display hides them client-side). The
        // suggestions call needs what's actually shown, not what was fetched.
        const displayIntent = mapStopsFilterToDisplayIntent(currentFilter);
        let contextIncludeDirect = displayIntent.IncludeDirect;
        let contextIncludeConnections = displayIntent.IncludeConnections;
        let contextStops = displayIntent.Stops;
        if (activeDirectiveFilters) {
          if (activeDirectiveFilters.includeDirect !== undefined) {
            contextIncludeDirect = activeDirectiveFilters.includeDirect;
          }
          if (activeDirectiveFilters.includeConnections !== undefined) {
            contextIncludeConnections = activeDirectiveFilters.includeConnections;
          }
          if (activeDirectiveFilters.stops !== undefined) {
            contextStops = activeDirectiveFilters.stops;
          }
        }

        const searchContext = {
          flightNumber: flightNumberValue ? [flightNumberValue] : [],
          dates: Array.isArray(datesValue) ? datesValue : [datesValue],
          includeDirect: contextIncludeDirect,
          includeConnections: contextIncludeConnections,
          ...(contextStops !== undefined ? { stops: contextStops } : {}),
        };
        const locale = (
          typeof navigator !== "undefined" && navigator.language
            ? navigator.language.split("-")[0]
            : "EN"
        ).toUpperCase();

        console.log("[FlightSuggestions Payload]", {
          searchContext,
          facets: responseFacets,
          appliedSuggestionId: activeAppliedSuggestionId,
          locale,
        });

        const suggestionsRequestId = ++suggestionsRequestSeq.current;
        fetchFlightSuggestions({
          searchContext,
          facets: responseFacets,
          appliedSuggestionId: activeAppliedSuggestionId,
          locale,
        })
          .then((res) => {
            if (suggestionsRequestId !== suggestionsRequestSeq.current) return;
            if (res && Array.isArray(res.suggestions)) {
              setSuggestions(res.suggestions);
            } else {
              setSuggestions([]);
            }
          })
          .catch(() => {
            if (suggestionsRequestId !== suggestionsRequestSeq.current) return;
            setSuggestions([]);
          });
      }
    } catch (error: any) {
      const apiMessage = error.response?.data?.message;
      const status = error.response?.status;
      // "No flights found" is a valid empty result, not an error — the inline
      // empty state already communicates it, so don't also raise a red toast.
      const isNoResults =
        status === 404 ||
        (typeof apiMessage === "string" &&
          /no flights?\s+found|no\s+results?/i.test(apiMessage));
      if (!isNoResults) {
        const message = apiMessage || "Failed to fetch flights.";
        showToast(message, "error", 3000);
        setSearchError(message);
      }
      setFlights([]);
      setConnectionFlights([]);
      setTotalFlights(0);
      setApiResultCount(0);
      setDirectTotal(undefined);
      setConnectionTotal(undefined);
      setShowResults(false);
    } finally {
      setIsLoadingFlights(false);
    }
  };

  // Fetches every page of the current search so "Select all" can select
  // results beyond the page currently on screen. Reuses the current
  // filters/sort/search params, only varying PageNumber.
  const fetchAllFlightsAndConnections = useCallback(async (): Promise<{
    flights: FlightDetails[];
    connections: ConnectionFlight[];
  }> => {
    if (totalPages <= 1) {
      return { flights, connections: connectionFlights };
    }

    let Stops: number | undefined;
    let IncludeDirect: boolean | undefined;
    let IncludeConnections: boolean | undefined;

    if (stopsFilter === "none") {
      IncludeDirect = true;
      IncludeConnections = true;
    } else if (stopsFilter === "one-or-fewer") {
      IncludeDirect = false;
      IncludeConnections = true;
    } else if (stopsFilter === "one") {
      Stops = 1;
    } else if (stopsFilter === "two") {
      Stops = 2;
    } else if (stopsFilter === "three") {
      Stops = 3;
    } else {
      IncludeDirect = true;
      IncludeConnections = false;
    }

    const baseParams = {
      FlightNumber: normalizeFlightNumber(flightNumber),
      Dates: datesInRange,
      PageSize: effectivePageSize,
      IncludeDirect,
      IncludeConnections,
      ...(Stops !== undefined ? { Stops } : {}),
      ...(sortBy ? { SortBy: sortBy } : {}),
      ...(sortOrder ? { SortOrder: sortOrder } : {}),
    };

    const responses = await Promise.all(
      Array.from({ length: totalPages }, (_, index) =>
        fetchFlights({ ...baseParams, PageNumber: index + 1 }),
      ),
    );

    const rawFlights = responses.flatMap(
      (r) => (r.data.items || []) as FlightDetails[],
    );
    const rawConnections = responses.flatMap(
      (r) => (r.data.connections || []) as ConnectionFlight[],
    );

    const allFlights = withDirectFlightCompositeIds(rawFlights);
    const allConnections = rawConnections.map((connection) => {
      const segmentsWithIds = connection.segments.map((segment) => {
        const baseId = segment.id ?? `${Date.now()}-${Math.random()}`;
        const departureTime = segment.departureTime;
        const compositeId = departureTime
          ? `${baseId}_${departureTime.replace(/[^0-9]/g, "")}`
          : String(baseId);
        return { ...segment, compositeId };
      });
      const connectionCompositeId = segmentsWithIds.length
        ? `conn_${segmentsWithIds.map((s) => s.compositeId).join("_")}`
        : `conn_${connection.totalDuration}`;
      return {
        ...connection,
        segments: segmentsWithIds,
        compositeId: connectionCompositeId,
      };
    });

    return { flights: allFlights, connections: allConnections };
  }, [
    totalPages,
    flights,
    connectionFlights,
    stopsFilter,
    sortBy,
    sortOrder,
    flightNumber,
    datesInRange,
    effectivePageSize,
  ]);

  const handleSearchClick = async () => {
    const newErrors: typeof errors = {};
    if (!flightNumber) {
      newErrors.flightNumber = "*Flight number is required";
    }

    if (datesInRange.length === 0)
      newErrors.date = "*Please select at least one date.";

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      // Only show the search results area when validation passes
      setHasSearched(true);
      onShowSearchResults?.(true);
      prevPageNumber.current = 1; // mark page 1 so the effect doesn't refetch
      setPageNumber(1); // Reset to first page on new search
      try {
        // getFlights toggles isLoadingFlights, which drives the button spinner.
        // No global loader here so only the button shows loading (like Route).
        // Save the recent search in parallel with the flight search.
        await Promise.all([
          getFlights({ stopsFilter: "none" }),
          (flightNumber && flightNumber.trim() !== ""
            ? saveRecentSearch({
                flightNumber: flightNumber.trim(),
              })
            : Promise.resolve()
          ).then(fetchRecentFlightNumberSearches),
        ]);

        // Save to localStorage after successful search
        saveLastFlightSearchToStorage(flightNumber, datesInRange);

        // Scroll to search results after a brief delay to ensure content is rendered
        setTimeout(() => {
          const searchResultsElement =
            document.getElementById("search-results");
          if (searchResultsElement) {
            searchResultsElement.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        }, 100);
      } catch (error) {
        console.error("Error fetching flights:", error);
      }
    }
  };

  const handleClearSearch = () => {
    setFlightNumber("");
    setSelectedDates([]);
    setDatesInRange([]);
    setRange([null, null]);
    setFlights([]);
    setConnectionFlights([]);
    setStopsFilter("none");
    setSortBy(null);
    setSortOrder(null);
    setSuggestions([]);
    setAppliedSuggestionId(null);
    setAppliedSuggestion(null);
    setFacets(null);
    setAppliedDirectiveFilters(null);
    setPriorManualState(null);
    setErrors({});
    setHasSearched(false);
    prevPageNumber.current = null; // reset so the next search starts clean
    setPageNumber(1);
    setTotalFlights(0);
    setApiResultCount(0);
    setDirectTotal(undefined);
    setConnectionTotal(undefined);
    onShowSearchResults?.(false);
  };

  const getDatesBetween = (start: Date, end: Date) => {
    const dates: string[] = [];
    const current = new Date(start);
    while (current <= end) {
      dates.push(current.toISOString().split("T")[0]);
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  const handleChange = (update: [Date | null, Date | null]) => {
    setRange(update);
    if (update[0] && update[1]) {
      setDatesInRange(getDatesBetween(update[0], update[1]));
    } else {
      setDatesInRange([]);
    }
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      const dateStr = formatDateLocal(date);
      const dateIndex = selectedDates.findIndex(
        (d) => formatDateLocal(d) === dateStr
      );

      if (dateIndex > -1) {
        // Date already selected, remove it
        const newDates = selectedDates.filter(
          (_, index) => index !== dateIndex
        );
        setSelectedDates(newDates);
      } else {
        // Add new date
        const newDates = [...selectedDates, date].sort(
          (a, b) => a.getTime() - b.getTime()
        );
        setSelectedDates(newDates);
      }

      // Clear date error when a date is selected
      if (errors.date) {
        setErrors((prev) => ({ ...prev, date: undefined }));
      }
    }
  };

  const handleSaveDates = (
    overrideDatesInRange?: string[],
    overrideSelectedDates?: Date[]
  ) => {
    const datesToSave =
      overrideDatesInRange ?? selectedDates.map((d) => formatDateLocal(d));
    const selDates = overrideSelectedDates ?? selectedDates;

    setDatesInRange(datesToSave);
    setSelectedDates(selDates);
    setIsCalendarOpen(false);

    // Auto-trigger search if all fields are filled (mirrors Search by Route).
    if (flightNumber && datesToSave.length > 0) {
      setTimeout(async () => {
        try {
          setHasSearched(true);
          onShowSearchResults?.(true);
          prevPageNumber.current = 1; // mark page 1 so the effect doesn't refetch
          setPageNumber(1);
          setErrors({});

          await getFlights({
            flightNumber,
            dates: datesToSave,
            pageNumber: 1,
          });

          saveLastFlightSearchToStorage(flightNumber, datesToSave);

          setTimeout(() => {
            const searchResultsElement =
              document.getElementById("search-results");
            if (searchResultsElement) {
              searchResultsElement.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }
          }, 100);
        } catch (error) {
          console.error("Error fetching flights:", error);
        }
      }, 100);
    }
  };

  const clearAllDates = () => {
    setSelectedDates([]);
    setDatesInRange([]);
  };

  const searchResults = useMemo(() => {
    if (!hasSearched) return null;
    const resultCount =
      (Number(totalFlights) || flights.length) + connectionFlights.length;
    return (
      <div id="search-results" className="pt-2 sm:pt-4">
        {/* Back + breadcrumb */}
        <div className="flex items-center gap-2 mt-2 mb-1 px-1">
          <button
            type="button"
            className="flex items-center justify-center w-8 h-8 rounded-lg bg-white shadow-sm hover:bg-[#F3F4F6] transition-colors"
            onClick={() => {
              handleClearSearch();
              onBack?.();
            }}
          >
            <svg className="w-4 h-4 text-[#1F2937]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <span className="text-[#1F2937] text-sm font-bold">{breadcrumbLabel}</span>
          <span className="text-gray-400 text-xs">&gt;</span>
          <span className="text-[#1F2937] text-sm font-medium">Flight No.</span>
        </div>

        <SearchResults
          flights={flights}
          connectionFlights={connectionFlights}
          stopsFilter={stopsFilter}
          onStopsFilterChange={handleStopsFilterChange}
          sortBy={sortBy}
          sortOrder={sortOrder as DurationSortOrder | null}
          onSortChange={handleSortChange}
          isLoadingFlights={isLoadingFlights}
          searchError={searchError}
          noResultsMessage={noResultsMessage}
          show={true}
          searchDate={date}
          selectedDates={datesInRange}
          onPostRequestSuccess={(items, options) => {
            // A post from a single flight card keeps the results and the search
            // criteria on screen; only the bottom-bar post returns home.
            if (options?.keepResults) {
              onPostRequestSuccess?.(items, options);
              return;
            }
            handleClearSearch();
            onPostRequestSuccess?.(items, options);
          }}
          sourceLabel={flightNumber}
          totalResults={totalFlights}
          apiResultCount={apiResultCount}
          directTotal={directTotal}
          connectionTotal={connectionTotal}
          onChangeDate={() => setIsCalendarOpen(true)}
          onChangeRoute={() => setShowFlightSuggestions(true)}
          changeRouteLabel="Change flight number"
          onWidenSearch={() => setOpenFilterSignal((s) => (s ?? 0) + 1)}
          onFetchAllForSelection={fetchAllFlightsAndConnections}
          appliedSuggestionId={appliedSuggestionId}
          appliedSuggestion={appliedSuggestion}
          onDismissSuggestion={handleDismissSuggestion}
          suggestions={suggestions}
          hideConnectedFilter={true}
          hideInternalFilters={true}
          showSortInHeader={true}
          readOnly={readOnly}
          page={PageNumber}
          totalPages={totalPages}
          onPageChange={setPageNumber}
        />
      </div>
    );
  }, [
    hasSearched,
    flights,
    connectionFlights,
    stopsFilter,
    sortBy,
    sortOrder,
    handleSortChange,
    appliedSuggestionId,
    appliedSuggestion,
    handleDismissSuggestion,
    suggestions,
    isLoadingFlights,
    onShowSearchResults,
    PageNumber,
    totalPages,
    totalFlights,
    apiResultCount,
    directTotal,
    connectionTotal,
    fetchAllFlightsAndConnections,
    date,
    datesInRange,
    onPostRequestSuccess,
  ]);

  return (
    <div className="mt-0">
      <style>{datePickerStyles}</style>

      {/* Tabs + Form — matching Screenshot 2 & 3 design */}
      <div className="sticky top-0 z-20 relative pt-2 pb-2">
        <div className="bg-[#050505] p-3.5 sm:p-4 rounded-xl shadow-md border border-black/80 relative z-10">
          {/* Top Pill Tab Bar */}
          <div className="mb-3 flex items-center justify-between">
            {tabButtons}
          </div>

          {/* White Card Container */}
          <div className="bg-white p-4 sm:p-5 overflow-visible rounded-xl shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <p className="text-[#374151] text-sm md:text-base font-semibold">
                Search by Flight Number
              </p>
              <div className="flex items-center gap-3 shrink-0 ml-auto">
                {(flightNumber || selectedDates.length > 0) && (
                  <button
                    className="flex items-center gap-1 text-[#6B7280] hover:text-[#4B5563] transition-colors"
                    onClick={handleClearSearch}
                    type="button"
                  >
                    <i className="st-close text-xs"></i>
                    <span className="text-xs">Clear Search</span>
                  </button>
                )}
                <RecentSearchesDropdown
                  label="Recent Searches"
                  items={recentFlightNumberSearches}
                  isLoading={isLoadingRecentSearches}
                  isOpen={isRecentSearchesOpen}
                  setOpen={setIsRecentSearchesOpen}
                  onOpen={fetchRecentFlightNumberSearches}
                  onSelect={handleRecentSearchSelect}
                  renderItem={(item) => {
                    const hasRoute = !!(item.sourceCode && item.destinationCode);
                    const hasFlight = !!item.flightNumber;

                    return (
                      <div className="flex items-center justify-between w-full py-0.5 font-inter">
                        {hasRoute ? (
                          <div className="flex items-center gap-3 text-sm sm:text-[15px]">
                            <span className="font-bold text-[#374151]">{item.sourceCode}</span>
                            <span className="text-gray-400 text-sm">→</span>
                            <span className="font-bold text-[#374151]">{item.destinationCode}</span>
                          </div>
                        ) : (
                          <span className="font-medium text-[#6B7280] text-sm sm:text-[15px]">
                            {item.flightNumber}
                          </span>
                        )}

                        {hasRoute && hasFlight && (
                          <span className="font-medium text-[#6B7280] text-sm sm:text-[15px] ml-auto">
                            {item.flightNumber}
                          </span>
                        )}
                      </div>
                    );
                  }}
                />
              </div>
            </div>

            {/* Fields in single row flex container */}
            <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4 overflow-visible">
              {/* Flight No. field */}
              <div className="relative w-full md:w-auto md:flex-[2] min-w-0 h-[52px] sm:h-[56px] px-4 py-2 border border-[#ECECED] rounded-2xl bg-white flex flex-col justify-center">
                <label className="flex items-center gap-1.5 text-[11px] sm:text-xs font-semibold font-inter text-[#4B5563] leading-none mb-0.5 shrink-0 cursor-pointer">
                  <i
                    className="st-flight text-white text-[13px] inline-block"
                    style={{
                      transform: "rotate(-50deg)",
                      WebkitTextStroke: "0.7px #4B5563",
                    }}
                  ></i>
                  Flight No
                </label>
                <div className="relative flex items-center min-h-[22px]" ref={flightFieldRef}>
                  <input
                    type="text"
                    value={flightNumber}
                    autoComplete="off"
                    onChange={(e) => {
                      setFlightNumber(e.target.value);
                      setShowFlightSuggestions(true);
                      setErrors((prev) => ({
                        ...prev,
                        flightNumber: undefined,
                        apiResponse: undefined,
                      }));
                    }}
                    onFocus={(e) => {
                      setShowFlightSuggestions(true);
                      e.target.select();
                    }}
                    placeholder="Search flight number"
                    className={`w-full p-0 pr-7 bg-transparent border-none shadow-none appearance-none font-inter ${
                      flightNumber && !showFlightSuggestions
                        ? "text-sm sm:text-base text-[#111827] font-bold"
                        : "text-sm sm:text-base text-[#374151] font-normal"
                    } placeholder-[#9CA3AF] focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-transparent leading-tight truncate`}
                    style={{ outline: "none", boxShadow: "none" }}
                  />
                  {flightNumber && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFlightNumber("");
                        setShowFlightSuggestions(true);
                      }}
                      className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-1"
                      aria-label="Clear flight number"
                    >
                      <i className="st-close text-xs"></i>
                    </button>
                  )}

                  {showFlightSuggestions && (
                    <div className="absolute top-full left-0 mt-2 bg-white border border-gray-100 rounded-2xl shadow-xl z-50 max-h-[350px] w-full sm:w-[320px] overflow-hidden flex flex-col animate-in fade-in slide-in-from-top-2 duration-150">
                      {/* Header with Close Button */}
                      <div className="bg-[#F3F4F6] px-4 py-3 border-b border-gray-200/60 flex items-center justify-between shrink-0">
                        <p className="text-sm font-semibold text-[#374151]">
                          Select Flight Number
                        </p>
                        <button
                          type="button"
                          onClick={() => setShowFlightSuggestions(false)}
                          className="text-gray-400 hover:text-black transition-colors p-0.5 rounded-lg"
                          aria-label="Close"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </div>

                      <div className="overflow-y-auto flex-1 p-2">
                        {isLoadingFlightNumbers ? (
                          <div className="flex justify-center items-center py-4">
                            <div className="w-5 h-5 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        ) : flightNumberOptions.length === 0 ? (
                          <div className="px-4 py-3 text-sm text-gray-400 text-center">
                            No flight numbers found
                          </div>
                        ) : (
                          <div className="space-y-0.5">
                            {flightNumberOptions.map((opt) => {
                              const isSelected = opt.value === flightNumber;
                              return (
                                <button
                                  key={opt.value}
                                  type="button"
                                  onClick={() => {
                                    setFlightNumber(opt.value);
                                    setShowFlightSuggestions(false);
                                    setErrors((prev) => ({
                                      ...prev,
                                      flightNumber: undefined,
                                      apiResponse: undefined,
                                    }));
                                    if (selectedDates.length === 0) {
                                      setTimeout(
                                        () => setIsCalendarOpen(true),
                                        300
                                      );
                                    }
                                  }}
                                  className={`w-full px-3.5 py-2.5 text-left transition-all border-b border-gray-100/60 last:border-b-0 ${
                                    isSelected
                                      ? "bg-[#FFFBEB] rounded-xl font-medium"
                                      : "hover:bg-gray-50 rounded-xl"
                                  }`}
                                >
                                  <div className="flex flex-col">
                                    <span className="text-sm font-bold text-gray-900 leading-tight">
                                      {opt.label}
                                    </span>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Date field */}
              <div className="w-full md:w-auto md:flex-1 min-w-0">
                <DateRangeCalendar
                  selectedDates={selectedDates}
                  datesInRange={datesInRange}
                  isCalendarOpen={isCalendarOpen}
                  setIsCalendarOpen={setIsCalendarOpen}
                  onDateChange={handleDateChange}
                  onSaveDates={handleSaveDates}
                  onClearDates={clearAllDates}
                  error={
                    selectedDates.length > 0 || datesInRange.length > 0
                      ? undefined
                      : errors.date
                  }
                  popperPlacement="bottom-end"
                  portalId="datepicker-portal"
                  className="h-[52px] sm:h-[56px] border border-[#ECECED] bg-white rounded-2xl"
                  showModeSwitcher={!readOnly}
                  minDate={readOnly ? new Date(Date.now() - 86400000) : undefined}
                  maxDate={readOnly ? new Date(Date.now() + 86400000) : undefined}
                />
              </div>

              {/* Find Your Flight + AI Spark Button */}
              <div className="flex items-center gap-2 shrink-0 w-full md:w-auto">
                <button
                  type="button"
                  className={`flex-1 md:flex-none px-6 rounded-2xl text-[#192227] text-center bg-[#FFBC00] hover:bg-[#F0AF00] text-sm sm:text-base font-semibold font-inter capitalize flex items-center justify-center gap-2 transition-colors shadow-sm cursor-pointer whitespace-nowrap h-[52px] sm:h-[56px] ${
                    isLoadingFlights ? "opacity-75 cursor-not-allowed" : ""
                  }`}
                  onClick={handleSearchClick}
                  disabled={isLoadingFlights}
                >
                  {isLoadingFlights ? (
                    <>
                      <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                      <span>Searching...</span>
                    </>
                  ) : (
                    <>
                      <Search className="w-[18px] h-[18px] text-[#192227] stroke-[2.5]" />
                      Find Your Flight
                    </>
                  )}
                </button>

                {/* AI Spark / Voice Button */}
                <button
                  type="button"
                  aria-label="Voice or AI Search"
                  className="w-[52px] h-[52px] sm:w-[56px] sm:h-[56px] bg-black hover:bg-gray-900 text-white rounded-2xl flex items-center justify-center shrink-0 transition-colors shadow-sm relative group cursor-pointer"
                >
                  <div className="relative flex items-center justify-center">
                    <img src={MicIcon} alt="" aria-hidden="true" className="w-6 h-7 object-contain" />
                  </div>
                </button>
              </div>
            </div>

              {/* Filter Sort bar bottom row */}
              <div className="flex flex-wrap items-center gap-4 mt-4">
                <FilterSortBar
                  stopsFilter={stopsFilter}
                  onStopsFilterChange={handleStopsFilterChange}
                  hideConnectedFilter={true}
                  sortBy={sortBy}
                  sortOrder={sortOrder as DurationSortOrder | null}
                  onSortOrderChange={(order, newSortBy) =>
                    handleSortChange(newSortBy ?? (order ? "duration" : null), order)
                  }
                  showSort={hasSearched}
                  openFilterSignal={openFilterSignal}
                  displayedResultCount={
                    hasSearched
                      ? (Number(totalFlights) || flights.length) + connectionFlights.length
                      : undefined
                  }
                />
              </div>
            </div>
          </div>
        </div>

        {/* AI Suggestive Filters - Placed directly below the black search container */}
        {hasSearched && (flights.length > 0 || connectionFlights.length > 0) && (
          <div className="-mt-3.5 sm:-mt-4 relative z-0 mb-1 px-3.5 sm:px-4">
            <AiSuggestiveFilters
              suggestions={suggestions}
              appliedSuggestionId={appliedSuggestionId}
              onSelectSuggestion={handleSelectSuggestion}
              onDismissSuggestion={handleDismissSuggestion}
              hasDirect={flights.length > 0}
              hasConnections={
                (typeof connectionTotal === "number" && connectionTotal > 0) ||
                connectionFlights.length > 0
              }
              isLoading={isLoadingFlights}
            />
          </div>
        )}

      {/* Search results — rendered below the search area */}
      <div className="pt-1">{searchResults}</div>
    </div>
  );
};

export default SearchFlightsNumber;
